import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import 'iconify-icon';
import { aboutData } from '../data/mock';

const About = () => {
  const { t } = useTranslation();
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('active');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.animate-on-scroll').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" className="relative py-24 border-t border-neutral-900" style={{ background: 'rgba(10, 10, 10, 0.72)' }}>
      <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          {/* Sticky Sidebar */}
          <div className="md:col-span-4 md:sticky md:top-24 md:self-start">
            <span className="text-xs font-mono uppercase tracking-[0.3em] text-accent-orange mb-4 block">
              {t('about.label')}
            </span>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6 text-[#EAEAEA] leading-[1.15] pb-1">
              {t('about.title')}
            </h2>
            <p className="text-base text-neutral-400 leading-relaxed">
              {t('about.description')}
            </p>
          </div>

          {/* Values Grid */}
          <div className="md:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            {aboutData.values.map((value, index) => {
              const valueKeys = ['about.values.excellence', 'about.values.transformation', 'about.values.innovation', 'about.values.ethics'];
              const titleKey = `${valueKeys[index]}.title`;
              const descKey = `${valueKeys[index]}.description`;
              return (
              <div
                key={index}
                className="group glow-card p-8 rounded-2xl hover-lift cursor-pointer animate-on-scroll relative overflow-hidden"
                style={{ transitionDelay: `${index * 0.1}s` }}
                onMouseMove={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = e.clientX - rect.left;
                  const y = e.clientY - rect.top;
                  e.currentTarget.style.setProperty('--x', `${x}px`);
                  e.currentTarget.style.setProperty('--y', `${y}px`);
                }}
              >
                {/* Big outlined number — bottom right watermark */}
                <span
                  aria-hidden="true"
                  className="absolute -right-2 -bottom-6 text-[7rem] md:text-[8rem] font-bold leading-none select-none pointer-events-none transition-all duration-500 group-hover:opacity-30 group-hover:-translate-y-1"
                  style={{
                    color: 'transparent',
                    WebkitTextStroke: '1px rgba(255, 103, 0, 0.18)',
                    opacity: 0.55,
                  }}
                >
                  0{index + 1}
                </span>

                <div className="relative z-10 flex items-start justify-between mb-5">
                  <div className="w-12 h-12 rounded-full bg-[#1F1F1F] border border-[#333333] flex items-center justify-center text-accent-orange group-hover:scale-110 group-hover:border-accent-orange transition-all duration-300">
                    <iconify-icon
                      icon={index === 0 ? "solar:star-linear" :
                            index === 1 ? "solar:bolt-circle-linear" :
                            index === 2 ? "solar:lightbulb-linear" :
                            "solar:shield-check-linear"}
                      className="text-2xl"
                    ></iconify-icon>
                  </div>
                  <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-accent-orange/80 mt-3">
                    0{index + 1} / 04
                  </span>
                </div>

                <h3 className="relative z-10 text-2xl font-semibold mb-3 tracking-tight text-[#EAEAEA]">
                  {t(titleKey, { defaultValue: value.title })}
                </h3>
                <p className="relative z-10 text-sm text-neutral-400 leading-[1.7]">
                  {t(descKey, { defaultValue: value.description })}
                </p>
              </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;